/**
 * A placed photo only carries a preview URL when it was placed by our own editor (copied from
 * the album listing's `urlSqCrop` at placement time — see build-placed-photo-shape.ts /
 * fill-shape-with-photo.ts) or previously resolved by this module.
 */

import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { CandidDetail } from '@/api/types/photo'
import { resolveCanvasPhotoUrl } from '@/lib/photos/resolve-canvas-photo-url'

export function shapeNeedsImagePreviewResolution(shape: ShapeData): boolean {
  return Boolean(shape.photoFill?.imageId) && !shape.photoFill?.previewUrl
}

/** The image identity to resolve for a shape flagged by `shapeNeedsImagePreviewResolution`. */
export function imageIdNeedingResolution(shape: ShapeData): string | undefined {
  return shape.photoFill?.imageId
}

/**
 * Builds the ShapeData patch to apply once a candid detail lookup resolves. Returns `null` when
 * the detail has no usable preview URL (e.g. the candid was since deleted).
 */
export function imagePreviewPatchFromCandidDetail(
  shape: ShapeData,
  detail: CandidDetail
): Partial<ShapeData> | null {
  const previewUrl = resolveCanvasPhotoUrl(detail)

  if (!previewUrl || !shape.photoFill) return null

  return {
    photoFill: {
      ...shape.photoFill,
      previewUrl,
      displayName: shape.photoFill.displayName || detail.displayName,
    },
  }
}
